import { useEffect } from "react";
import { notification } from "antd";

import {
  startForegroundMessageListener,
  syncAdminFcmToken,
} from "@/services/firebaseWebPushService";
import { markNotificationInboxAsRead } from "@/services/notificationInboxService";
import history from "@/utils/history";
import { reservationDetailPath } from "@/utils/routes";

const resolvePayloadText = (payload = {}) => {
  const strTitle =
    payload?.notification?.title ||
    payload?.data?.title ||
    "New Notification";
  const strBody =
    payload?.notification?.body ||
    payload?.data?.body ||
    "You have a new update.";
  return {
    strTitle,
    strBody,
  };
};

const parseDeepLinkPayload = (payload = {}) => {
  const data = payload?.data || {};
  const deepLinkRaw = data?.deepLink;

  let deepLink = {};
  if (typeof deepLinkRaw === "string" && deepLinkRaw.trim()) {
    try {
      const parsed = JSON.parse(deepLinkRaw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        deepLink = parsed;
      }
    } catch (_) {}
  } else if (deepLinkRaw && typeof deepLinkRaw === "object") {
    deepLink = deepLinkRaw;
  }

  return {
    ...deepLink,
    screen: `${deepLink?.screen || data?.screen || ""}`.trim(),
    bookingId: `${deepLink?.bookingId || data?.bookingId || ""}`.trim(),
    reservationId: `${deepLink?.reservationId || data?.reservationId || ""}`.trim(),
    taskId: `${deepLink?.taskId || data?.taskId || ""}`.trim(),
    paymentId: `${deepLink?.paymentId || data?.paymentId || ""}`.trim(),
    notificationId: `${data?.notificationId || data?.inboxId || ""}`.trim(),
  };
};

const resolveForegroundNavigationPath = (payload = {}) => {
  const deepLink = parseDeepLinkPayload(payload);
  const bookingId = deepLink.bookingId || deepLink.reservationId;

  if (deepLink.taskId) {
    return `/tasks/board?taskId=${encodeURIComponent(String(deepLink.taskId))}`;
  }
  if (bookingId) {
    return reservationDetailPath(String(bookingId));
  }
  if (deepLink.paymentId) {
    return "/payments";
  }
  if (deepLink.notificationId) {
    return `/notifications?notificationId=${encodeURIComponent(
      String(deepLink.notificationId)
    )}`;
  }
  return "/notifications";
};

const markInboxNotificationAsReadSilently = async (notificationId = "") => {
  const normalizedId = `${notificationId || ""}`.trim();
  if (!normalizedId) return;

  try {
    await markNotificationInboxAsRead(normalizedId);
    window.dispatchEvent(new Event("notifications:changed"));
  } catch (_) {
    // ignore mark-as-read errors for passive foreground notification clicks
  }
};

const markNotificationFromCurrentUrl = () => {
  if (typeof window === "undefined") return;

  try {
    const url = new URL(window.location.href);
    const notificationId = `${url.searchParams.get("notificationId") || ""}`.trim();
    if (!notificationId) return;

    markInboxNotificationAsReadSilently(notificationId);
    url.searchParams.delete("notificationId");
    window.history.replaceState(
      {},
      document.title,
      `${url.pathname}${url.search}${url.hash}`
    );
  } catch (_) {
    // ignore malformed URL errors
  }
};

export default function useAdminWebPushMessaging({ isEnabled }) {
  useEffect(() => {
    if (!isEnabled) return () => {};

    let unsubscribe = () => {};
    let blnDisposed = false;

    const syncTokenSilently = () => {
      syncAdminFcmToken({ forceSync: false }).catch(() => null);
    };

    const initializeMessaging = async () => {
      markNotificationFromCurrentUrl();

      try {
        const unsubscriber = await startForegroundMessageListener((payload) => {
          const { strTitle, strBody } = resolvePayloadText(payload);
          const deepLink = parseDeepLinkPayload(payload);
          const targetPath = resolveForegroundNavigationPath(payload);
          window.dispatchEvent(new Event("notifications:changed"));
          notification.open({
            message: strTitle,
            description: strBody,
            duration: 5,
            placement: "topRight",
            onClick: () => {
              if (deepLink.notificationId) {
                markInboxNotificationAsReadSilently(deepLink.notificationId);
              }
              history.push(targetPath);
            },
          });
        });
        if (!blnDisposed) {
          unsubscribe = typeof unsubscriber === "function" ? unsubscriber : () => {};
        }
      } catch (error) {
        // ignore listener errors; token sync can still run independently
      }

      syncTokenSilently();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncTokenSilently();
      }
    };

    initializeMessaging();
    window.addEventListener("focus", syncTokenSilently);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      blnDisposed = true;
      window.removeEventListener("focus", syncTokenSilently);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      unsubscribe();
    };
  }, [isEnabled]);
}
